import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useDemo } from "@/contexts/DemoContext";
import { useToast } from "@/hooks/use-toast";

interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  related_id: string | null;
  related_type: string | null;
  is_read: boolean;
  created_at: string;
}

// Debounce helper
function debounce<T extends (...args: unknown[]) => void>(fn: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return ((...args: unknown[]) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  }) as T;
}

/** Fetches user notifications with real-time subscriptions, mark-as-read, and delete support. */
export const useNotifications = () => {
  const { user } = useAuth();
  const { isDemoMode } = useDemo();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const lastToastRef = useRef<string | null>(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from("notifications")
      .select("id, user_id, type, title, message, is_read, created_at, related_id, related_type")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      console.error("Error fetching notifications:", error);
      return;
    }

    setNotifications(data || []);
    setUnreadCount(data?.filter((n) => !n.is_read).length || 0);
    setIsLoading(false);
  }, [user]);

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    // Demo mode: just update local state
    if (isDemoMode) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      return;
    }

    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("id", notificationId);

    if (!error) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    if (!user) return;

    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("user_id", user.id)
      .eq("is_read", false);

    if (!error) {
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId: string) => {
    const notification = notifications.find((n) => n.id === notificationId);
    
    // Demo mode: just update local state
    if (isDemoMode) {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      if (notification && !notification.is_read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
      return;
    }

    const { error } = await supabase
      .from("notifications")
      .delete()
      .eq("id", notificationId);

    if (!error) {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      if (notification && !notification.is_read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    }
  };

  // Debounced fetch to prevent rapid refetches
  const debouncedFetch = useMemo(
    () => debounce(fetchNotifications, 1000),
    [fetchNotifications]
  );

  // Subscribe to realtime notifications - consolidated into single channel
  useEffect(() => {
    if (!user) return;

    fetchNotifications();

    // Defensively remove any stale channel from a prior mount (StrictMode /
    // fast remount can leave a subscribed channel cached by topic, which
    // makes a subsequent .on() throw "cannot add postgres_changes callbacks
    // ... after subscribe()").
    const topicPrefix = `realtime:user-notifications-${user.id}`;
    for (const existing of supabase.getChannels()) {
      if (existing.topic.startsWith(topicPrefix)) {
        supabase.removeChannel(existing);
      }
    }

    // Unique channel name per mount avoids re-using a cached, already-
    // subscribed channel instance.
    const channelName = `user-notifications-${user.id}-${
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2)
    }`;

    // Single consolidated channel for all notification-related updates
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as Notification;
          setNotifications((prev) => [newNotification, ...prev]);
          setUnreadCount((prev) => prev + 1);
          
          // Prevent duplicate toasts for same notification
          if (lastToastRef.current !== newNotification.id) {
            lastToastRef.current = newNotification.id;
            toast({
              title: newNotification.title,
              description: newNotification.message,
            });
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const updatedNotification = payload.new as Notification;
          setNotifications((prev) =>
            prev.map((n) => (n.id === updatedNotification.id ? updatedNotification : n))
          );
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const deletedId = (payload.old as { id: string }).id;
          setNotifications((prev) => prev.filter((n) => n.id !== deletedId));
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "assignments",
          filter: `student_id=eq.${user.id}`,
        },
        (payload) => {
          const updated = payload.new as { status: string; title: string };
          if (updated.status === "reviewed") {
            toast({
              title: "Assignment Reviewed",
              description: `Your assignment "${updated.title}" has been reviewed!`,
            });
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "casting_submissions",
          filter: `actor_id=eq.${user.id}`,
        },
        (payload) => {
          const updated = payload.new as { status: string };
          if (updated.status === "accepted") {
            toast({
              title: "Congratulations!",
              description: "Your casting submission has been accepted!",
            });
          } else if (updated.status === "rejected") {
            toast({
              title: "Submission Update",
              description: "Your casting submission status has been updated.",
              variant: "destructive",
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchNotifications, toast, debouncedFetch]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch: fetchNotifications,
  };
};
